import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { formatApiError } from "../api";

const LoginPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from?.pathname || "/admin";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("✓");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" data-testid="login-page">
      <div className="px-6 md:px-12 py-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/55 hover:text-[#FFDE00]"
          data-testid="login-home-link"
        >
          <ArrowLeft size={14} /> {t.auth.home}
        </Link>
        <div className="font-display text-2xl tracking-tight">
          TEAM ASTANA<span className="text-[#FFDE00]">.</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="kicker mb-4">/admin</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-10">
            {t.auth.login_title}
          </h1>

          <div className="space-y-7">
            <div>
              <label className="text-[10px] tracking-widest uppercase text-white/45">
                {t.auth.email}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field-input"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-widest uppercase text-white/45">
                {t.auth.password}
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field-input"
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <div
              className="mt-6 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary mt-8 w-full"
            data-testid="login-submit"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> {t.auth.logging_in}
              </>
            ) : (
              <>
                <LogIn size={16} /> {t.auth.login_btn}
              </>
            )}
          </button>

          <div className="mt-8 text-center text-sm text-white/55">
            <Link to="/register" className="hover:text-[#FFDE00]" data-testid="login-to-register">
              {t.auth.to_register}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
